import * as React from 'react'
import { ButtonSize, PrimaryButton } from 'streamr-ui'
import { MqDesktop, MqTablet, Tablet } from 'streamr-ui/css/consts'
import styled from 'styled-components'
import Br from '~shared/Br'
import Figure from '~shared/Figure'
import { Section, SectionContent, SectionCta, SectionTitle } from '../../Section'
import animation from '../assets/animation.mp4'
import poster from '../assets/poster.jpg'
import poster2x from '../assets/poster@2x.jpg'

export function Economy() {
    return (
        <EconomyRoot>
            <SectionContent>
                <SectionTitle>Inside the DATA economy</SectionTitle>
                <VideoWithCaptions />
                <SectionCta>
                    <LinkedBlockLabel>Dive into Tokenomics docs to learn more</LinkedBlockLabel>
                    <div>
                        <PrimaryButton
                            size={ButtonSize.Moose}
                            tag="a"
                            href="https://docs.streamr.network/streamr-network/incentives/network-incentives"
                        >
                            Tokenomics docs
                        </PrimaryButton>
                    </div>
                </SectionCta>
            </SectionContent>
        </EconomyRoot>
    )
}

const LinkedBlockLabel = styled.div`
    font-size: 16px;
    line-height: 26px;
    font-weight: 500;
    flex-grow: 1;
`

const VideoWrapper = styled.div`
    position: relative;
    height: 224px;
    width: 400px;

    video,
    img {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        height: 100%;
    }

    @media (max-width: ${Tablet - 1}px) {
        &::before {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            width: 32px;
            z-index: 1;
            background-color: #eeeeee;
            mask-image: linear-gradient(to left, transparent, black);
        }

        &::after {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            right: 0;
            width: 32px;
            z-index: 1;
            background-color: #eeeeee;
            mask-image: linear-gradient(to right, transparent, black);
        }
    }

    @media ${MqTablet} {
        height: 464px;
        width: 824px;
    }

    @media ${MqDesktop} {
        height: 680px;
        width: 1208px;
    }
`

const AnimationWrapper = styled.div`
    background-color: #eeeeee;
    border-radius: 8px;
    overflow: hidden;
    position: relative;
    margin: 3.75rem auto 0;
    /* fixes video pushing out of rounded corners in Safari */
    transform: translateZ(0);
    display: flex;
    justify-content: center;

    @media ${MqTablet} {
        margin-top: 5.5rem;
    }

    @media ${MqDesktop} {
        margin-top: 7.5rem;
    }
`

const CaptionsWrapper = styled.div`
    display: grid;
`

const Slide = styled.div`
    grid-row: 1;
    grid-column: 1;
    opacity: 0;
    transform: translateY(6px);
    transition: opacity 180ms ease-in-out, transform 180ms ease-in-out;

    &[data-active='true'] {
        opacity: 1;
        transform: translateY(0);
    }
`

function getSlides(round, dt) {
    return {
        [round * 10 + 1]: [dt + 0.1, dt + 4.25],
        [round * 10 + 2]: [dt + 5.35, dt + 10.5],
        [round * 10 + 3]: [dt + 11, dt + 14.3],
        [round * 10 + 4]: [dt + 15.7, dt + 20],
        [round * 10 + 5]: [dt + 21, dt + 25.5],
        [round * 10 + 6]: [dt + 26.3, dt + 29.7],
        [round * 10 + 7]: [dt + 30.5, dt + 34.5],
        [round * 10 + 8]: [dt + 35.7, dt + 40.25],
        [round * 10 + 9]: [dt + 41.5, dt + 45],
    }
}

const slideTimings = {
    ...getSlides(0, 0),
    ...getSlides(1, 49.7),
}

const VideoWithCaptions = () => {
    const videoRef = React.useRef()

    const [playing, setPlaying] = React.useState(false)

    const [activeSlide, setActiveSlide] = React.useState()

    React.useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => setPlaying(entry.isIntersecting))

        observer.observe(videoRef.current)

        // Remove the observer as soon as the component is unmounted
        return () => {
            observer.disconnect()
        }
    }, [])

    React.useEffect(() => {
        if (videoRef.current) {
            if (playing) {
                videoRef.current.play()
            } else {
                videoRef.current.pause()
            }
        }
    }, [playing])

    const onTimeUpdate = React.useCallback(() => {
        if (!videoRef.current) {
            return
        }

        const { currentTime } = videoRef.current
        const visibleSlide = Object.keys(slideTimings).find((slide) => {
            const [start, end] = slideTimings[slide]

            return currentTime >= start && currentTime <= end
        })

        setActiveSlide(
            typeof slideTimings[visibleSlide] === 'object' ? Number(visibleSlide) % 10 : undefined,
        )
    }, [])

    return (
        <Figure>
            <AnimationWrapper>
                <VideoWrapper>
                    <img src={poster} srcSet={poster2x} alt="Network tokenomics" />
                    <video
                        muted
                        autoPlay
                        loop
                        playsInline
                        onTimeUpdate={onTimeUpdate}
                        ref={videoRef}
                        preload="metadata"
                    >
                        <source src={animation} type="video/mp4" />
                    </video>
                </VideoWrapper>
            </AnimationWrapper>
            <Figure.Caption>
                <CaptionsWrapper>
                    <Slide data-active={activeSlide === 1}>
                        A Publisher App publishes a stream,
                        <Br mobile />
                        and their node relays it to subscribers
                    </Slide>
                    <Slide data-active={activeSlide === 2}>
                        Wanting better service for the stream,
                        <Br mobile />a Sponsor deploys and funds a Sponsorship
                    </Slide>
                    <Slide data-active={activeSlide === 3}>
                        Streamr nodes watching for sponsorships
                        <Br mobile />
                        stake DATA tokens to join the stream
                    </Slide>
                    <Slide data-active={activeSlide === 4}>
                        Streamr Nodes relay the stream for
                        <Br mobile />
                        an agreed period of time
                    </Slide>
                    <Slide data-active={activeSlide === 5}>
                        After which they claim rewards in
                        <Br mobile />
                        DATA tokens from the Sponsorship smart contract
                    </Slide>
                    <Slide data-active={activeSlide === 6}>
                        Node operators are limited in the number of streams
                        <Br mobile />
                        they can join by the size of their Operator Contract
                    </Slide>
                    <Slide data-active={activeSlide === 7}>
                        Delegators pay DATA tokens into the Operator Contract
                    </Slide>
                    <Slide data-active={activeSlide === 8}>
                        Streamr Nodes have the funds
                        <Br mobile />
                        to join more streams
                    </Slide>
                    <Slide data-active={activeSlide === 9}>
                        And Delegators share in
                        <Br mobile />
                        the Node rewards
                    </Slide>
                </CaptionsWrapper>
            </Figure.Caption>
        </Figure>
    )
}

const EconomyRoot = styled(Section)`
    --SectionCta_Margin: 56px auto 0;

    color: #0c009a;

    ${SectionTitle} {
        text-align: center;
    }

    ${Figure.Caption} {
        color: #a3a3a3;
        margin-top: 2rem;
        font-size: 0.875rem;
        text-align: center;
    }

    @media ${MqTablet} {
        --SectionCta_Margin: 88px auto 0;

        ${Figure.Caption} {
            font-size: 1.125rem;
            margin-top: 2.5rem;
        }
    }

    @media ${MqDesktop} {
        --SectionCta_Margin: 136px auto 0;

        ${Figure.Caption} {
            font-size: 1.5rem;
            margin-top: 4.5rem;
        }
    }
`
